import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { setTenantBrand } from '../config/brand';
import { useSessionTimer } from '../hooks/useSessionTimer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// iter60: interceptor globale — toast user-friendly per 403 permessi mancanti
let _permToastShown = 0;
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 403 && err.response.data?.section) {
      const now = Date.now();
      // dedupe: max 1 toast ogni 3s
      if (now - _permToastShown > 3000) {
        _permToastShown = now;
        const { section, level } = err.response.data;
        toast.error(`Permesso negato: non hai i diritti per ${level === 'edit' ? 'modificare' : 'visualizzare'} la sezione "${section}"`);
      }
    }
    return Promise.reject(err);
  }
);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleSessionExpired = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.warn('Logout API failed (will still clear local session):', err?.message);
    }
    setUser(false);
  }, []);

  const session = useSessionTimer(handleSessionExpired);
  const { start: startSession, clear: clearSession, isExpired, persistLogin } = session;

  const logout = useCallback(async () => {
    await handleSessionExpired();
    clearSession();
  }, [handleSessionExpired, clearSession]);

  const checkAuth = useCallback(async () => {
    if (isExpired()) {
      clearSession();
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      setTenantBrand(response.data.tenant || {});
      startSession();
    } catch (err) {
      console.warn('Auth check failed (likely no session):', err?.response?.status || err?.message);
      setUser(false);
      clearSession();
    } finally {
      setLoading(false);
    }
  }, [isExpired, clearSession, startSession]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true },
    );
    setUser(response.data);
    setTenantBrand(response.data.tenant || {});
    persistLogin();
    return response.data;
  }, [persistLogin]);

  const register = useCallback(async (email, password, name) => {
    const response = await axios.post(
      `${API}/auth/register`,
      { email, password, name, role: 'operaio' },
      { withCredentials: true },
    );
    return response.data;
  }, []);

  const contextValue = useMemo(
    () => ({ user, loading, login, register, logout, checkAuth }),
    [user, loading, login, register, logout, checkAuth],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Si è verificato un errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
